<template>
  <div
    class="ai-assistant-panel fixed z-50 flex w-96 max-w-[90vw] flex-col overflow-hidden"
    :class="[
      anchored
        ? 'ai-assistant-panel--anchored rounded-r-2xl'
        : 'bg-surface-base right-4 bottom-4 max-h-[80vh] rounded-2xl shadow-2xl',
      { 'ai-assistant-panel--resizing': resizing }
    ]"
  >
    <div
      class="flex shrink-0 items-center justify-between p-4 dark:border-neutral-700"
    >
      <h2 class="flex items-center gap-2 text-lg font-bold">
        <AiAssistantAvatar
          :expression="isSending ? 'thinking' : 'idle'"
          :playful="!$route.meta.avatarSerious"
          :size="28"
        />
        {{ $t('global.ai_assistant.title', 'Assistant IA') }}
      </h2>
      <div class="flex items-center gap-3">
        <button
          v-if="isDev"
          type="button"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-white"
          title="Charger une conversation de test (dev only)"
          @click="$emit('load-mock')"
        >
          <FontAwesomeIcon :icon="['fal', 'wand-magic-sparkles']" />
        </button>
        <button
          v-if="dockable"
          type="button"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-white"
          :title="
            anchored
              ? $t('global.ai_assistant.undock', 'Détacher')
              : $t('global.ai_assistant.dock', 'Ancrer')
          "
          @click="$emit('toggle-dock')"
        >
          <FontAwesomeIcon
            :icon="['fal', anchored ? 'up-right-from-square' : 'sidebar-flip']"
          />
        </button>
        <button
          type="button"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-white"
          @click="$emit('close')"
        >
          <FontAwesomeIcon :icon="['fal', 'times']" />
        </button>
      </div>
    </div>
    <AiAssistantConversation />
    <div
      v-if="anchored"
      class="absolute inset-y-0 left-0 z-10 w-2 cursor-col-resize touch-none"
      @mousedown="startResize"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import AiAssistantAvatar from '@/views/layouts/main-layout/ai-assistant/AiAssistantAvatar.vue'
import AiAssistantConversation from '@/views/layouts/main-layout/ai-assistant/AiAssistantConversation.vue'

export default {
  name: 'AiAssistantPanel',
  components: { AiAssistantAvatar, AiAssistantConversation, FontAwesomeIcon },
  props: {
    anchored: {
      type: Boolean,
      required: false,
      default: false
    },
    dockable: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['close', 'toggle-dock', 'resize', 'load-mock'],
  data() {
    return {
      resizing: false,
      isDev: import.meta.env.DEV
    }
  },
  computed: {
    ...mapGetters({ isSending: 'aiAssistant/isSending' })
  },
  methods: {
    startResize(event) {
      event.preventDefault()
      const startX = event.clientX
      const startWidth = this.$el.getBoundingClientRect().width
      this.resizing = true
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'

      const onMouseMove = (moveEvent) => {
        this.$emit('resize', startWidth + (startX - moveEvent.clientX))
      }
      const onMouseUp = () => {
        this.resizing = false
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
        window.removeEventListener('mousemove', onMouseMove)
        window.removeEventListener('mouseup', onMouseUp)
      }
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)
    }
  }
}
</script>

<style scoped>
.ai-assistant-panel {
  height: 32rem;
  transition:
    right 250ms ease,
    bottom 250ms ease,
    width 250ms ease,
    height 250ms ease,
    border-radius 250ms ease,
    background-color 250ms ease,
    box-shadow 250ms ease;
}

.ai-assistant-panel--resizing {
  transition: none;
}

@supports (anchor-name: --ai-assistant-anchor) {
  .ai-assistant-panel--anchored {
    position-anchor: --ai-assistant-anchor;
    right: anchor(--ai-assistant-anchor right);
    bottom: anchor(--ai-assistant-anchor bottom);
    width: anchor-size(--ai-assistant-anchor width);
    height: anchor-size(--ai-assistant-anchor height);
  }
}
</style>
